<template>
  <div class="mobile-button-container">
    <div class="mobile-button hide">
      <main-button
        v-if="!checkTrialLimit()"
        depressed
        :text="adminText('workflowsAll.add_attendee_dd630f2', 'Add attendee')"
        @click="
          goTo(
            isRegularOrganization()
              ? routes.organizer.createAccreditation
              : routes.sportOrganizer.createAccreditationSport,
            { eventId },
          )
        "
      />
    </div>
    <v-layout column>
      <v-flex class="title-screen" xs12>
        <span>{{ adminText('workflowsAll.attendee_list_87ae776', 'Attendee list') }}</span>
      </v-flex>
      <v-flex x4>
        <div class="title-description attendee-table-description">
          {{ adminText('workflowsAll.here_you_can_create_manage_and_search_attende_60e0cd3', 'Here you can create, manage and search attendees. Also, you can print accreditations for one, more or all of them. You can customize the list by choosing which columns to display - up to 10 of them.') }}
        </div>
      </v-flex>
      <v-flex class="mb-16 attendee-actions">
        <attendee-limit-field
          v-if="showAttendeeLimit"
          class="attendee-actions__limit"
          :event-id="eventId"
          :attendee-limit="attendeeLimit"
          :registered-count="registeredAttendeesCount"
          @updated="refetchEvent"
        />
        <v-tooltip v-if="checkTrialLimit()" class="desktop-button" right color="#979797">
          <main-button slot="activator" disabled :text="adminText('workflowsAll.add_attendee_dd630f2', 'Add attendee')" />
          <span>{{ adminText('workflowsAll.you_can_create_up_to_20_accreditations_in_tri_0b2a5bd', 'You can create up to 20 accreditations in trial version') }}</span>
        </v-tooltip>
        <main-button
          v-if="!checkTrialLimit()"
          depressed
          class="desktop-button"
          @click="
            goTo(
              isRegularOrganization()
                ? routes.organizer.createAccreditation
                : routes.sportOrganizer.createAccreditationSport,
              { eventId },
            )
          "
        >
          <span class="add-symbol">+</span>{{ adminText('workflowsAll.add_attendee_d1b4b9c', 'Add Attendee') }}
        </main-button>
        <main-button v-if="!['952', '2444', '4531', '8265'].includes(eventId)" flat @click="printAll()">
          <img class="print-icon-style" src="/assets/Print.svg" :alt="adminText('workflowsAll.print_icon_2378e41', 'Print icon')">{{ adminText('workflowsAll.print_all_e2be7c1', 'Print all') }}
        </main-button>
      </v-flex>
      <div class="attendee_table">
        <table-filters
          v-if="!showAlternateHeader"
          :visible-fields.sync="visibleFields"
          :all-fields="allFields"
          :initial-filters="{ quickSearch, ...filters }"
          :attendee-fields="attendeeFields"
          :categories="categories"
          :extra-modules="extraModules"
          :event-id="eventId"
          @quickSearch="handleQuickSearch"
          @filters="handleFilters"
        />
        <selected-attendees-header
          v-else
          :permission="permission"
          :selected="selected"
          :event-id="eventId"
          :user="user"
          @clear="selected = []"
          @print="print"
          @delete="showDeleteDialog = true"
          @openDemoModal="demoModal = true"
        />
        <table-list-skeleton v-if="isTableLoading" :show-filters="false" :visible-fields="visibleFields" />
        <template v-else>
          <div
            v-if="attendees.length === 0"
            class="no-attendees-message-container border__left border__right"
          >
            <span class="no-attendees-message">{{ adminText('workflowsAll.no_matching_records_found_0c7f0a6', 'No matching records found.') }}</span>
          </div>
          <table-list
            v-else
            :event-id="eventId"
            :organization-slug="organizationSlug"
            :selected.sync="selected"
            :attendees="attendees"
            :visible-fields="visibleFields"
            :attendee-fields="attendeeFields"
            :pagination="pagination"
            click-field="uuid"
            modern
            @click="
              goTo(
                isRegularOrganization()
                  ? routes.organizer.editAccreditation
                  : routes.sportOrganizer.editAccreditationSport,
                { accreditationUuid: $event },
              )
            "
            @sort="handleSort($event)"
            @attendeeDeleted="onAttendeeDeleted"
          />
          <table-pagination
            :pagination="pagination"
            :total-attendees="totalAttendeeCount"
            :per-page-options="perPageOptions"
            @change="handlePagination($event)"
            @changePerPage="handlePagination($event)"
          />
        </template>
      </div>
    </v-layout>
    <delete-dialog
      :content="workflowLabel((adminText('workflowsAll.are_you_sure_you_want_to_delete_selected_accr_260853d', 'Are you sure you want to delete selected accreditations? This operation cannot be undone.')))"
      :on-delete="deleteSelected"
      :show.sync="showDeleteDialog"
      :title="adminText('workflowsAll.delete_accreditations_b25367f', 'Delete accreditations')"
    />
    <simple-snackbar
      :open.sync="onPrintSnackbarOpen"
      :message-prop.sync="onPrintSnackbarDisplay"
      :color="onPrintSnackbarColor"
    />
    <demo-modal v-if="demoModal" :user="loggedUser" @close="demoModal = false" />
  </div>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import { difference } from 'lodash'

import TableFilters from './attendee-table/filters.vue'
import TableList from './attendee-table/list.vue'
import TableListSkeleton from './attendee-table/list-skeleton.vue'
import TablePagination from './attendee-table/pagination.vue'
import SelectedAttendeesHeader from './attendee-table/selected-attendees-header.vue'

import { DELETE_ATTENDEES } from '../../graphql/accreditation.graphql'
import {
  GET_EVENT_ACCREDITATIONS_ONLY,
  GET_EVENT_DETAILS_WITH_FILTERED_ATTENDEE_FIELDS,
} from '../../graphql/event.graphql'
import { BULK_CREATE_PRINT, PRINT_ALL } from '../../graphql/print.graphql'
import { GET_LOGGED_USER } from '../../graphql/user.graphql'
import { getAttendeeByBarcode } from '../../http'
import { getVisibleFields, objectToArray, serverURL, setVisibleFields } from '../../utils'
import SimpleSnackbar from '../shared/snackbars/simple-snackbar.vue'
import DeleteDialog from './../shared/dialogs/delete-dialog.vue'
import AttendeeLimitField from './attendee-limit-field.vue'
import { listOrderComparator } from './attendee-fields'
import DemoModal from '@/components/organizations/DemoModal.vue'

const DEFAULT_PER_PAGE = 10
const MAX_PER_PAGE = 100
const PER_PAGE_OPTIONS = [5, DEFAULT_PER_PAGE, 25, MAX_PER_PAGE]

export default {
  mixins: [WorkflowDisplay],
  name: 'accreditations-list-container',
  components: {
    TableFilters,
    TableList,
    TableListSkeleton,
    TablePagination,
    SelectedAttendeesHeader,
    DeleteDialog,
    SimpleSnackbar,
    DemoModal,
    AttendeeLimitField,
  },
  props: {
    eventId: String,
    user: Object,
    organizationSlug: String,
  },
  data() {
    const { pagination, quickSearch, filters } = this.getInitialFromQueryParams()

    return {
      attendees: [],
      totalAttendeeCount: 0,
      categories: [],
      attendeeFields: [],
      eventAttendeeFields: {},
      extraModules: {},
      selected: [],
      quickSearch,
      pagination,
      permission: '',
      url: '',
      snackbar: false,
      filters,
      onCopyInviteSnackbar: false,
      showDeleteDialog: false,
      onPrintSnackbarOpen: false,
      onPrintSnackbarMessage: '',
      onPrintSnackbarColor: '',
      designSet: true,
      paginationLoading: false,
      managed: '',
      quickPrint: false,
      barcode: '',
      onBarcodeError: false,
      serverURL,
      visibleFields: [],
      allFields: [],
      loggedUser: '',
      demoModal: false,
      perPageOptions: PER_PAGE_OPTIONS,
      attendeeLimit: null,
      registeredAttendeesCount: 0,
    }
  },
  computed: {
    onPrintSnackbarDisplay: {
      get() { return this.workflowLabel(this.onPrintSnackbarMessage) },
      set(value) { this.onPrintSnackbarMessage = value },
    },
    isTableLoading() {
      return this.$apollo.loading || this.paginationLoading
    },
    showAttendeeLimit() {
      return !!this.extraModules && this.extraModules.ticketing === false
    },
    showAlternateHeader() {
      return this.selected && this.selected.length > 0
    },
  },
  async mounted() {
    await this.fetchAttendees(this.pagination, this.filters, { quickSearch: this.quickSearch })
  },
  apollo: {
    getEvent: {
      query: GET_EVENT_DETAILS_WITH_FILTERED_ATTENDEE_FIELDS,
      variables() {
        return {
          eventId: this.eventId,
          status: 'ALLOWED',
        }
      },
      manual: true,
      async result({ data, loading }) {
        if (!loading && data) {
          const {
            getEventWithFilteredAttendeeFields: {
              builder,
              extraModules,
              categories,
              managed,
              attendeeFields,
              quickPrint,
              attendeeLimit,
              registeredAttendeesCount,
            },
          } = data
          this.attendeeLimit = attendeeLimit
          this.registeredAttendeesCount = registeredAttendeesCount || 0
          this.categories = categories || []
          this.attendeeFields = attendeeFields
          this.attendeeFields['Attendee name'] = {
            type: 'text',
            id: 'Attendee name',
          }
          this.eventAttendeeFields = objectToArray(attendeeFields, 'name').sort(listOrderComparator)
          this.extraModules = extraModules || {}
          this.designSet = !!builder
          this.managed = managed
          this.quickPrint = quickPrint
          this.allFields = [
            ...new Set([
              'Attendee name',
              ...this.eventAttendeeFields.map(f => f.name),
              'Ticket Buyer',
              'Buyer Email',
              'Created At',
            ]),
          ]
          this.allFields = this.allFields.filter(af => {
            return (
              af !== 'First name' && af !== 'Last name' && af !== 'First Name' && af !== 'Last Name'
            )
          })
          if (this.extraModules.categoriesAndZones) {
            this.allFields = [
              ...new Set([
                'Attendee name',
                ...this.eventAttendeeFields.map(f => f.name),
                'Category',
                'Ticket Buyer',
                'Buyer Email',
                'Created At',
              ]),
            ]
            this.allFields = this.allFields.filter(af => {
              return (
                af !== 'First name' &&
                af !== 'Last name' &&
                af !== 'First Name' &&
                af !== 'Last Name'
              )
            })
          }
          const localStorageFields = getVisibleFields(this.eventId)
          if (localStorageFields.length > 0) {
            if (difference(localStorageFields, this.allFields).length > 0) {
              // if there is a field in my local, and not in definition, it means that other user deleted it
              this.visibleFields = this.allFields
            } else {
              this.visibleFields = localStorageFields
            }
          } else {
            this.visibleFields = this.allFields
          }
          this.visibleFields = this.normalizeVisibleFields(this.visibleFields)
          this.visibleFields = this.visibleFields.filter(vf => {
            return (
              vf !== 'First name' && vf !== 'Last name' && vf !== 'First Name' && vf !== 'Last Name'
            )
          })
          setVisibleFields([...this.visibleFields.filter(f => !f.includes('?'))], this.eventId)
          const {
            data: {
              me: { email },
            },
          } = await this.$apollo.query({ query: GET_LOGGED_USER })
          this.loggedUser = email
        }
      },
    },
  },
  methods: {
    async handlePagination({ page, per_page }) {
      const { page: currentPage, per_page: currentPerPage } = this.pagination
      if (currentPage > page && currentPage === 1) return
      if (page > currentPage && !(this.totalAttendeeCount - currentPerPage * currentPage > 0))
        return

      const perPage = this.normalizePerPage(per_page)
      this.pagination = {
        ...this.pagination,
        per_page: perPage,
        page: page,
      }

      this.addPaginationToRoute(this.pagination)

      await this.fetchAttendees(this.pagination, this.filters, this.quickSearch)
    },
    async handleFilters(filters) {
      this.addFiltersToRoute(filters)
      this.filters = filters

      await this.fetchAttendees(this.pagination, this.filters, this.quickSearch)
    },
    async handleQuickSearch(quickSearch) {
      this.pagination = { ...this.pagination, page: 1 }
      this.addFiltersToRoute({ quickSearch })
      this.quickSearch = quickSearch

      await this.fetchAttendees(this.pagination, this.filters, this.quickSearch)
    },
    async handleSort(sortBy) {
      const { per_page, page, sortBy: thisSortBy, descending } = this.pagination
      const pagination = {
        page,
        per_page,
        sortBy,
        descending: sortBy === thisSortBy ? !descending : false,
      }
      this.addPaginationToRoute(pagination)
      this.pagination = pagination

      await this.fetchAttendees(
        pagination,
        this.filters,
        typeof this.quickSearch === 'string' ? { quickSearch: this.quickSearch } : this.quickSearch,
      )
    },
    getInitialFromQueryParams() {
      const { per_page, page, sortBy, descending, quickSearch, ...fieldFilters } = this.$route.query

      return {
        pagination: {
          page: parseInt(page, 10) || 1,
          per_page: this.normalizePerPage(per_page),
          sortBy: sortBy,
          descending: descending === 'true' ? true : false,
        },
        quickSearch: typeof quickSearch === 'string' && quickSearch !== '[object Object]' ? quickSearch : '',
        filters: fieldFilters || {},
      }
    },
    addPaginationToRoute({ page, per_page, sortBy, descending }) {
      this.$router.replace({
        query: {
          ...this.$route.query,
          per_page,
          page,
          sortBy,
          descending,
        },
      })
    },
    addFiltersToRoute(filters) {
      const filtersWithValueArray = Object.entries(filters).filter(f => f[1]) // remove entries with no value

      const filtersWithValue = filtersWithValueArray.reduce((acc, e) => {
        //handle problem with '/' in url
        if (e[0].includes('/')) {
          acc[e[0].replaceAll('/', '-')] = e[1]
        } else {
          acc[e[0]] = e[1]
        }

        return acc
      }, {})

      this.$router.replace({
        query: {
          ...filtersWithValue,
          ...this.pagination,
        },
      })
    },
    async onBarcodeScanned() {
      const { data, errors } = await getAttendeeByBarcode(this.barcode, this.eventId)
      if (data && !errors) {
        const { id, uuid } = data.getAttendeeByBarcode
        this.goTo(
          this.isRegularOrganization()
            ? this.routes.organizer.editAccreditation
            : this.routes.sportOrganizer.editAccreditationSport,
          { accreditationUuid: uuid || id },
        )
      } else {
        this.onBarcodeError = true
      }
      this.barcode = ''
    },
    toArray(array) {
      return Array.isArray(array) ? array : [array]
    },
    normalizePerPage(perPage) {
      const parsedPerPage = parseInt(perPage, 10)
      if (!Number.isFinite(parsedPerPage) || parsedPerPage < 1) {
        return DEFAULT_PER_PAGE
      }

      return Math.min(parsedPerPage, MAX_PER_PAGE)
    },
    areFiltersEmpty() {
      return Object.values(this.filters).some(f => f === undefined || f.length === 0)
    },
    checkTrialLimit() {
      return this.user.type === 'TRIAL' ? this.totalAttendeeCount >= 20 : false
    },
    async print() {
      const status = await this.$apollo.mutate({
        mutation: BULK_CREATE_PRINT,
        variables: {
          accreditationIds: this.selected,
          id: this.eventId,
        },
      })
      this.afterPrint(status.data.bulkCreatePrint)
    },
    afterPrint() {
      this.selected = []
      this.onPrintSnackbarOpen = true
      this.onPrintSnackbarMessage = 'workflowsAll.we_have_sent_you_an_email_with_your_accredita_83f8889'
      this.onPrintSnackbarColor = 'primary'
    },
    async printAll() {
      this.$apollo.mutate({
        mutation: PRINT_ALL,
        variables: {
          id: this.eventId,
        },
      })
      this.afterPrint()
    },
    async fetchAttendees(pagination, filters, quickSearch) {
      this.paginationLoading = true
      try {
        const { data } = await this.$apollo.query({
          query: GET_EVENT_ACCREDITATIONS_ONLY,
          variables: {
            eventId: this.eventId,
            status: 'ALLOWED',
            quickSearch:
              typeof quickSearch === 'string'
                ? quickSearch
                : (quickSearch && quickSearch.quickSearch) || '',
            visibleFields: this.visibleFields,
            pagination: pagination || this.pagination,
            filters: filters || this.filters || {},
          },
        })
        if (!data) {
          return
        }
        const { attendees, attendeeCount } = data.getEventAccreditations
        this.attendees = attendees
        this.attendees.map(a => {
          if (a.fields) {
            a.fields['Attendee name'] = this.getAttendeeName(a.fields)
          } else {
            a.fields = { 'Attendee name': 'workflowsAll.unknown_attendee_e946a3a' }
          }
        })

        this.totalAttendeeCount = parseInt(attendeeCount, 10)
      } finally {
        this.paginationLoading = false
      }
    },
    async deleteSelected() {
      await this.$apollo.mutate({
        mutation: DELETE_ATTENDEES,
        variables: { attendeeIds: this.selected, eventId: this.eventId },
      })
      await this.fetchAttendees()
      this.totalAttendeeCount = this.totalAttendeeCount - this.selected.length
      this.selected = []
      this.refetchEvent()
    },
    async onAttendeeDeleted() {
      await this.fetchAttendees(this.pagination, this.filters, this.quickSearch)
      this.refetchEvent()
    },
    refetchEvent() {
      return this.$apollo.queries.getEvent.refetch()
    },
    isRegularOrganization() {
      return this.$route.path.includes('/organizations/')
    },
    normalizeVisibleFields(fields) {
      const visibleFields = fields && fields.length ? fields : this.allFields
      const normalized = [
        'Attendee name',
        ...visibleFields.filter(vf => vf !== 'Attendee name' && this.allFields.includes(vf)),
      ]

      return normalized.slice(0, 10)
    },
    getAttendeeName(fields) {
      const firstName = fields['First name'] || fields['First Name'] || fields.Ime || fields.firstName
      const lastName = fields['Last name'] || fields['Last Name'] || fields.Prezime || fields.lastName
      const name = [firstName, lastName].filter(Boolean).join(' ').trim()

      return name || fields.name || 'workflowsAll.unknown_attendee_e946a3a'
    },
  },
}
</script>

<style scoped>
.attendee-table-description {
  max-width: 900px;
  margin-bottom: 28px;
}

.attendee_table {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
}

.no-attendees-message-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

.no-attendees-message {
  color: #979797;
  padding: 20px;
}

.border__left {
  border-left: 2px solid #f5f5f5;
}

.border__right {
  border-right: 2px solid #f5f5f5;
}

ul {
  list-style-type: none;
}

.err-line {
  margin-bottom: 8px;
}

.err-line span {
  font-weight: bold;
}

.add-symbol {
  font-size: 1.875rem;
  margin-right: 0.8125rem;
  font-weight: 200;
}

.attendee-actions {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
}

.attendee-actions__limit {
  margin-right: auto;
}

.print-icon-style {
  margin-right: 0.5rem;
}

.title-screen {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #ff4c00;
  margin-bottom: 1.5rem;
}

@media screen and (max-width: 800px) {
  .title-screen {
    display: none;
  }

  .mobile-button-container {
    margin-top: 1.5rem;
  }
}
</style>
